import React from 'react';
import type { Layouts } from 'react-grid-layout';
import type { ResultStrategy, WidgetDefinition, EvaluatePanelPropsBase } from './ResultStrategy';
import { WidgetWrapper } from '../WidgetWrapper';

function lazyMeepMeepWidget<T extends keyof typeof import('../MeepMeepWidgets')>(exportName: T) {
    return React.lazy(async () => {
        const module = await import('../MeepMeepWidgets');
        return { default: module[exportName] as React.ComponentType<any> };
    });
}

const MeepMeepSummaryWidget = lazyMeepMeepWidget('MeepMeepSummaryWidget');
const MeepMeepOverviewWidget = lazyMeepMeepWidget('MeepMeepOverviewWidget');
const MeepMeepFeatureImpactWidget = lazyMeepMeepWidget('MeepMeepFeatureImpactWidget');
const MeepMeepPruneCandidatesWidget = lazyMeepMeepWidget('MeepMeepPruneCandidatesWidget');
const MeepMeepBranchReviewWidget = lazyMeepMeepWidget('MeepMeepBranchReviewWidget');
const MeepMeepWeightEvolutionWidget = lazyMeepMeepWidget('MeepMeepWeightEvolutionWidget');
const MeepMeepDayDetailWidget = lazyMeepMeepWidget('MeepMeepDayDetailWidget');
const MeepMeepR2Widget = lazyMeepMeepWidget('MeepMeepR2Widget');
const MeepMeepPositionWidget = lazyMeepMeepWidget('MeepMeepPositionWidget');
const MeepMeepDailyPnLWidget = lazyMeepMeepWidget('MeepMeepDailyPnLWidget');
const MeepMeepHeatmapWidget = lazyMeepMeepWidget('MeepMeepHeatmapWidget');
const MeepMeepAlphaScatterWidget = lazyMeepMeepWidget('MeepMeepAlphaScatterWidget');
const MeepMeepDayPnLChartWidget = lazyMeepMeepWidget('MeepMeepDayPnLChartWidget');

function renderLazyWidget(node: React.ReactNode) {
    return (
        <React.Suspense
            fallback={
                <div style={{ padding: '1.25rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Loading widget…
                </div>
            }
        >
            {node}
        </React.Suspense>
    );
}

export class MeepMeepStrategy implements ResultStrategy {
    id = 'meepmeep';

    match(nature?: string): boolean {
        return nature === 'meepmeep_result';
    }

    getAvailableWidgets(_hasAiReport: boolean): WidgetDefinition[] {
        return [
            { id: 'summary', label: '📊 Summary', icon: '📊' },
            { id: 'overview', label: '📈 Overview PnL', icon: '📈' },
            { id: 'r2', label: '📉 R² Gain', icon: '📉' },
            { id: 'feature-impact', label: '⚖️ Feature Impact', icon: '⚖️' },
            { id: 'prune-candidates', label: '✂️ Prune Candidates', icon: '✂️' },
            { id: 'branch-review', label: '🧩 Branch Review', icon: '🧩' },
            { id: 'weight-evolution', label: '🧬 Weight Evolution', icon: '🧬' },
            { id: 'day-detail', label: '🔍 Decision Context', icon: '🔍' },
            { id: 'day-position', label: '📈 Position & Trades', icon: '📈' },
            { id: 'day-pnl-chart', label: '💹 Intraday PnL Chart', icon: '💹' },
            { id: 'daily-pnl', label: '📅 Daily PnL', icon: '📅' },
            { id: 'heatmap', label: '🌡️ Performance Heatmap', icon: '🌡️' },
            { id: 'alpha-efficacy', label: '🎯 Signal Efficacy', icon: '🎯' },
        ];
    }

    getDefaultWidgets(): string[] {
        return ['summary', 'overview', 'r2', 'feature-impact', 'prune-candidates', 'branch-review', 'weight-evolution', 'day-detail', 'day-position', 'day-pnl-chart', 'daily-pnl', 'heatmap', 'alpha-efficacy'];
    }

    getLayout(): Layouts {
        return {
            lg: [
                { i: 'overview', x: 0, y: 0, w: 9, h: 6 },
                { i: 'summary', x: 9, y: 0, w: 3, h: 6 },
                { i: 'feature-impact', x: 0, y: 6, w: 5, h: 7 },
                { i: 'prune-candidates', x: 5, y: 6, w: 3, h: 7 },
                { i: 'branch-review', x: 8, y: 6, w: 4, h: 7 },
                { i: 'weight-evolution', x: 0, y: 13, w: 12, h: 6 },
                { i: 'day-detail', x: 0, y: 19, w: 6, h: 6 },
                { i: 'day-position', x: 6, y: 19, w: 6, h: 6 },
                { i: 'day-pnl-chart', x: 0, y: 25, w: 12, h: 5 },
                { i: 'daily-pnl', x: 0, y: 30, w: 12, h: 4 },
                { i: 'r2', x: 0, y: 34, w: 12, h: 5 },
                { i: 'alpha-efficacy', x: 0, y: 39, w: 12, h: 6 },
                { i: 'heatmap', x: 0, y: 45, w: 12, h: 6 },
            ],
        };
    }

    getStats(_data: any, _backendStats?: Record<string, number | null> | null): React.ReactNode {
        // Handled by SummaryWidget for more complex layout
        return null;
    }

    renderWidget(widgetId: string, props: EvaluatePanelPropsBase): React.ReactNode {
        // MeepMeep payload structure:
        // result.result_views.overview -> data for overview chart
        // result.calibration -> weights
        // result.stats -> summary stats

        const calibration = props.result?.calibration;
        const stats = props.result?.stats || props.backendStats;

        switch (widgetId) {
            case 'summary':
                return (
                    <WidgetWrapper title="📊 MeepMeep Performance Summary">
                        {renderLazyWidget(<MeepMeepSummaryWidget stats={stats} />)}
                    </WidgetWrapper>
                );
            case 'overview':
                return (
                    <WidgetWrapper title="📈 Performance Overview (PnL Breakdown)">
                        {renderLazyWidget(
                            <MeepMeepOverviewWidget
                                data={props.result?.chart_data || []}
                                onSelectDate={props.setSelectedMeepMeepDate}
                                selectedDate={props.selectedMeepMeepDate}
                            />
                        )}
                    </WidgetWrapper>
                );
            case 'r2':
                return (
                    <WidgetWrapper title="📉 Recursive R² Gain">
                        {renderLazyWidget(<MeepMeepR2Widget data={props.result?.chart_data || []} />)}
                    </WidgetWrapper>
                );
            case 'feature-impact':
                return (
                    <WidgetWrapper title="⚖️ Feature Impact">
                        {renderLazyWidget(<MeepMeepFeatureImpactWidget calibration={calibration} />)}
                    </WidgetWrapper>
                );
            case 'prune-candidates':
                return (
                    <WidgetWrapper title="✂️ Prune Candidates">
                        {renderLazyWidget(<MeepMeepPruneCandidatesWidget calibration={calibration} />)}
                    </WidgetWrapper>
                );
            case 'branch-review':
                return (
                    <WidgetWrapper title="🧩 Branch Review">
                        {renderLazyWidget(<MeepMeepBranchReviewWidget calibration={calibration} />)}
                    </WidgetWrapper>
                );
            case 'weight-evolution':
                return (
                    <WidgetWrapper title="🧬 Weight Evolution">
                        {renderLazyWidget(<MeepMeepWeightEvolutionWidget calibration={calibration} />)}
                    </WidgetWrapper>
                );
            case 'day-detail':
                return (
                    <WidgetWrapper title={`🔍 Decision Context ${props.selectedMeepMeepDate ? `- ${props.selectedMeepMeepDate}` : ''}`}>
                        {renderLazyWidget(
                            <MeepMeepDayDetailWidget
                                date={props.selectedMeepMeepDate ?? null}
                                strategy={props.rootNode}
                                strategyPayload={props.meepMeepRequestTree ?? null}
                                globalStart={props.globalStart}
                                globalEnd={props.globalEnd}
                            />
                        )}
                    </WidgetWrapper>
                );
            case 'day-position':
                return (
                    <WidgetWrapper title={`📈 Position & Trades ${props.selectedMeepMeepDate ? `- ${props.selectedMeepMeepDate}` : ''}`}>
                        {renderLazyWidget(
                            <MeepMeepPositionWidget
                                date={props.selectedMeepMeepDate ?? null}
                                strategy={props.rootNode}
                                strategyPayload={props.meepMeepRequestTree ?? null}
                                globalStart={props.globalStart}
                                globalEnd={props.globalEnd}
                            />
                        )}
                    </WidgetWrapper>
                );
            case 'day-pnl-chart':
                return (
                    <WidgetWrapper title={`💹 Intraday PnL Breakdown ${props.selectedMeepMeepDate ? `- ${props.selectedMeepMeepDate}` : ''}`}>
                        {renderLazyWidget(
                            <MeepMeepDayPnLChartWidget
                                date={props.selectedMeepMeepDate ?? null}
                                strategy={props.rootNode}
                                strategyPayload={props.meepMeepRequestTree ?? null}
                                globalStart={props.globalStart}
                                globalEnd={props.globalEnd}
                            />
                        )}
                    </WidgetWrapper>
                );
            case 'heatmap':
                return (
                    <WidgetWrapper title="🌡️ Performance Heatmap (Time vs Day)">
                        {renderLazyWidget(<MeepMeepHeatmapWidget data={props.result?.chart_data || []} />)}
                    </WidgetWrapper>
                );
            case 'alpha-efficacy':
                return (
                    <WidgetWrapper title="🎯 Signal Efficacy (Alpha vs Realized Move)">
                        {renderLazyWidget(<MeepMeepAlphaScatterWidget data={props.result?.chart_data || []} />)}
                    </WidgetWrapper>
                );
            case 'daily-pnl':
                return (
                    <WidgetWrapper title="📅 Daily PnL Distribution">
                        {renderLazyWidget(
                            <MeepMeepDailyPnLWidget
                                data={props.result?.chart_data || []}
                                onSelectDate={props.setSelectedMeepMeepDate}
                            />
                        )}
                    </WidgetWrapper>
                );
            default:
                return null;
        }
    }
}
